import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook that detects the theme of content underneath a navigation element
 * and applies the appropriate theme to the nav for optimal contrast.
 * 
 * @param {string} navId - Optional ID of the navigation element (defaults to first .glass-nav)
 * @param {Object} options - Configuration options
 * @param {string} options.fallbackTheme - Fallback theme ('light' or 'dark'), defaults to 'dark'
 * @param {number} options.probeOffset - Offset from nav bottom for probe positioning, defaults to 0
 * @param {number} options.debounceDelay - Debounce delay in ms, defaults to 10
 * @returns {void}
 */
export function useNavThemeOnBackground(navId, options = {}) {
  const {
    fallbackTheme = 'dark',
    probeOffset = 0,
    debounceDelay = 10
  } = options;

  const probeRef = useRef(null);
  const observerRef = useRef(null);
  const navRef = useRef(null);
  const currentThemeRef = useRef(fallbackTheme);
  const debounceTimerRef = useRef(null);

  const updateNavTheme = useCallback((theme) => {
    if (currentThemeRef.current === theme) return;
    
    currentThemeRef.current = theme;
    if (navRef.current) {
      navRef.current.dataset.theme = theme;
    }
  }, []);

  const createProbe = useCallback(() => {
    // Remove existing probe if it exists
    if (probeRef.current) {
      probeRef.current.remove();
    }

    // Find navigation element
    const nav = navId 
      ? document.getElementById(navId)
      : document.querySelector('.glass-nav');
    
    if (!nav) {
      console.warn('Navigation element not found');
      return;
    }

    navRef.current = nav;

    // Create probe element
    const probe = document.createElement('div');
    probe.setAttribute('data-nav-probe', 'true');
    probe.style.cssText = `
      position: fixed;
      width: 1px;
      height: 1px;
      left: 50%;
      transform: translateX(-50%);
      z-index: -1;
      pointer-events: none;
      opacity: 0;
    `;

    // Position probe at the bottom of the nav
    const navRect = nav.getBoundingClientRect();
    probe.style.top = `${navRect.bottom + probeOffset}px`;

    document.body.appendChild(probe);
    probeRef.current = probe;

    return probe;
  }, [navId, probeOffset]);

  const setupIntersectionObserver = useCallback(() => {
    if (!('IntersectionObserver' in window)) {
      console.warn('IntersectionObserver not supported, using fallback theme');
      updateNavTheme(fallbackTheme);
      return;
    }

    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const probe = probeRef.current;
    if (!probe) return;

    // Create intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        // Clear existing debounce timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          // Find the most visible section
          let maxRatio = 0;
          let dominantTheme = fallbackTheme;

          entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
              maxRatio = entry.intersectionRatio;
              const target = entry.target;
              const theme = target.dataset.theme;
              if (theme === 'light' || theme === 'dark') {
                dominantTheme = theme;
              }
            }
          });

          updateNavTheme(dominantTheme);
        }, debounceDelay);
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.1, 0.5, 0.9, 1]
      }
    );

    // Observe all sections with data-theme attribute
    const sections = document.querySelectorAll('[data-theme]');
    sections.forEach(section => {
      observer.observe(section);
    });

    observerRef.current = observer;
  }, [fallbackTheme, updateNavTheme, debounceDelay]);

  const updateProbePosition = useCallback(() => {
    if (!navRef.current || !probeRef.current) return;

    const navRect = navRef.current.getBoundingClientRect();
    probeRef.current.style.top = `${navRect.bottom + probeOffset}px`;
  }, [probeOffset]);

  useEffect(() => {
    // Initial setup
    const probe = createProbe();
    if (!probe) return;

    setupIntersectionObserver();

    // Handle window resize
    const handleResize = () => {
      updateProbePosition();
    };

    // Handle scroll to update probe position if nav changes size
    const handleScroll = () => {
      updateProbePosition();
    };

    // Setup event listeners
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      
      if (probeRef.current) {
        probeRef.current.remove();
      }
      
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [createProbe, setupIntersectionObserver, updateProbePosition]);

  // Re-setup observer when DOM changes (for dynamic content)
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      const hasThemeChanges = mutations.some(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          return true;
        }
        if (mutation.type === 'childList') {
          const hasNewThemeSections = Array.from(mutation.addedNodes).some(
            node => node instanceof HTMLElement && node.hasAttribute('data-theme')
          );
          return hasNewThemeSections;
        }
        return false;
      });

      if (hasThemeChanges) {
        setupIntersectionObserver();
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-theme'],
      childList: true,
      subtree: true
    });

    return () => {
      observer.disconnect();
    };
  }, [setupIntersectionObserver]);
}